/**
 * Translates the Space-local grid's column filters into
 * `SpaceLocalAttributesInput` fields (DV-495). "Value set" has no
 * server-side predicate — the backend's `valueSetFilter` was deferred, per
 * document-vault-backend's DV-494 spec — so that column stays unfiltered.
 */
import type { AgGrid } from "@jsq/ds/core";
import { dataTypeToValueType } from "#common/graphql/Space";
import type { AttrDataType } from "#common/types/vault";
import type {
  AttributeValueType,
  DomainSlug,
  QualityContract,
  SpaceLocalAttributeNameFilter,
  SpaceLocalAttributeOrderBy,
  VaultAttributeFilterOperator,
} from "#graphql/supergraphTypes";

/** `colId`s the grid puts a server-side filter on — see `SpaceLocalAttributesGrid`. */
export const NAME_FILTER_COL_ID = "name";
export const KIND_FILTER_COL_ID = "dataType";
export const SPACE_FILTER_COL_ID = "space";
export const DOMAIN_FILTER_COL_ID = "domain";
export const REQUIREMENT_FILTER_COL_ID = "requirement";

/** "Requirement" column display label -> `QualityContract`. */
export const REQUIREMENT_FILTER_LABELS: Record<string, QualityContract> = {
  Required: "guaranteed",
  Optional: "ingested",
};

/** The backend's legal `nameFilter` operators — any other value fails validation server-side. */
export const NAME_FILTER_OPERATORS: Record<string, VaultAttributeFilterOperator> = {
  equals: "equals",
  notEqual: "not_equal",
  contains: "contains",
  notContains: "not_contains",
  startsWith: "starts_with",
  endsWith: "ends_with",
};

const SUBSTRING_OPERATORS: ReadonlySet<VaultAttributeFilterOperator> = new Set([
  "contains",
  "not_contains",
  "starts_with",
  "ends_with",
]);

// Mirrors document-vault-backend's substring-length guard on `nameFilter`.
const MIN_SUBSTRING_LENGTH = 3;
const MAX_SUBSTRING_LENGTH = 200;

// The backend rejects any Unicode "Cc" control character in `nameFilter`
// with a VALIDATION_ERROR that fails the whole query — drop the filter here
// instead, the same defensive stance as the substring-length gate below.
// eslint-disable-next-line no-control-regex -- deliberately matching C0/C1 controls, mirroring the backend's unicodedata.category(ch) == "Cc" check
const CONTROL_CHAR = /[\u0000-\u001f\u007f-\u009f]/;

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

const nameFilterFromModel = (model: unknown): SpaceLocalAttributeNameFilter | null => {
  // The column pins `maxNumConditions: 1`, so a combined model means the
  // control was widened — one `SpaceLocalAttributeNameFilter` is one predicate.
  if (!isObject(model) || Array.isArray(model.conditions)) return null;
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- model.type is an opaque AG Grid filter-model field, defensively stringified
  const operator = NAME_FILTER_OPERATORS[String(model.type ?? "")];
  if (!operator) return null;
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- model.filter is an opaque AG Grid filter-model field, defensively stringified
  const raw = model.filter == null ? "" : String(model.filter);
  if (!raw || CONTROL_CHAR.test(raw)) return null;
  if (SUBSTRING_OPERATORS.has(operator)) {
    const trimmedLength = raw.trim().length;
    if (trimmedLength < MIN_SUBSTRING_LENGTH || trimmedLength > MAX_SUBSTRING_LENGTH)
      return null;
  }
  return { operator, value: raw };
};

const valueTypesFromModel = (model: unknown): AttributeValueType[] | null => {
  if (!isObject(model) || !Array.isArray(model.values)) return null;
  return model.values
    .filter((v): v is string => typeof v === "string")
    .map((v) => dataTypeToValueType(v as AttrDataType))
    .filter((vt): vt is AttributeValueType => vt != null);
};

/**
 * A set-filter model's ticked display names, resolved through `nameToId`.
 * Returns `null` only when the column has no filter applied at all — an
 * active-but-unresolvable selection (every value unticked, or every ticked
 * name absent from the lookup) returns `[]`, which callers must treat as
 * "matches nothing," not "unfiltered" (see `matchesNoRows`).
 */
const idsFromSetModel = <T extends string>(
  model: unknown,
  nameToId: Record<string, T>,
): T[] | null => {
  if (!isObject(model) || !Array.isArray(model.values)) return null;
  return model.values
    .filter((v): v is string => typeof v === "string")
    .map((name) => nameToId[name])
    .filter((id): id is T => id != null);
};

export type SpaceLocalFiltersFromModel = {
  nameFilter: SpaceLocalAttributeNameFilter | null;
  valueTypes: AttributeValueType[] | null;
  spaceIds: string[] | null;
  domains: DomainSlug[] | null;
  qualityContracts: QualityContract[] | null;
};

export type SpaceLocalFilterLookups = {
  spaceIdByName: Record<string, string>;
  domainSlugByName: Record<string, DomainSlug>;
};

export const spaceLocalFiltersFromFilterModel = (
  filterModel: AgGrid.FilterModel | null | undefined,
  { spaceIdByName, domainSlugByName }: SpaceLocalFilterLookups,
): SpaceLocalFiltersFromModel => {
  if (!filterModel) {
    return {
      nameFilter: null,
      valueTypes: null,
      spaceIds: null,
      domains: null,
      qualityContracts: null,
    };
  }
  return {
    nameFilter: nameFilterFromModel(filterModel[NAME_FILTER_COL_ID]),
    valueTypes: valueTypesFromModel(filterModel[KIND_FILTER_COL_ID]),
    spaceIds: idsFromSetModel(filterModel[SPACE_FILTER_COL_ID], spaceIdByName),
    domains: idsFromSetModel(filterModel[DOMAIN_FILTER_COL_ID], domainSlugByName),
    qualityContracts: idsFromSetModel(
      filterModel[REQUIREMENT_FILTER_COL_ID],
      REQUIREMENT_FILTER_LABELS,
    ),
  };
};

/**
 * True when Data type, Space, Domain, or Requirement is an ACTIVE set filter
 * (per `idsFromSetModel`'s null/`[]` distinction above) that resolves to
 * zero ids — every value unticked, or every ticked value unresolvable —
 * which must be treated as "matches nothing," not "unfiltered."
 */
export const matchesNoRows = ({
  valueTypes,
  spaceIds,
  domains,
  qualityContracts,
}: SpaceLocalFiltersFromModel): boolean =>
  [valueTypes, spaceIds, domains, qualityContracts].some(
    (ids) => ids !== null && ids.length === 0,
  );

/**
 * Translate an AG Grid sort model into a `SpaceLocalAttributeOrderBy` (DV-497,
 * DV-670, DV-687). Only the first sort item is honored. Attribute (name),
 * Data type, Space, Domain, and Requirement are the only clickable
 * server-sortable columns. An empty/absent sort model (and any unsupported
 * column) falls through to `created_at_desc`, the table's default.
 */
export const spaceLocalOrderByFromSortModel = (
  sortModel: AgGrid.SortModelItem[] | null | undefined,
): SpaceLocalAttributeOrderBy => {
  const first = sortModel?.[0];
  const defaultOrder = "created_at_desc" as SpaceLocalAttributeOrderBy;
  if (!first) return defaultOrder;
  const asc = first.sort === "asc";
  if (first.colId === NAME_FILTER_COL_ID)
    return asc ? "attribute_label_asc" : "attribute_label_desc";
  if (first.colId === KIND_FILTER_COL_ID)
    return asc ? "attribute_value_type_asc" : "attribute_value_type_desc";
  if (first.colId === SPACE_FILTER_COL_ID) return asc ? "space_name_asc" : "space_name_desc";
  if (first.colId === DOMAIN_FILTER_COL_ID) return asc ? "domain_asc" : "domain_desc";
  if (first.colId === REQUIREMENT_FILTER_COL_ID)
    return asc ? "quality_contract_asc" : "quality_contract_desc";
  return defaultOrder;
};
